import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import axiosInstance from '../components/axiosInstance';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const WebAuthnAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { email, isInitialSetup, sessionToken } = location.state || {};
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(isInitialSetup ? "register" : "login");
  const [webAuthnSupported, setWebAuthnSupported] = useState(true);
  const employeeFormsRef = useRef(null); // ✅ Add a reference
  const { setUser } = useContext(AuthContext);

  const handleSuccessfulLogin = ({ token, user }) => {
    if (!login) {
      return;
    }
    
    try {
      // Store critical data in localStorage for immediate access
      localStorage.setItem('token', token);
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('employeeId', user.employeeId || '');
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('justLoggedIn', 'true');
      
      // Call the context login function as backup
      login(token, user.role, user.store, user.employeeId);
      
      // Determine where to go based on role
      const redirectPath = (user.role === 'admin' || user.role === 'manager')
        ? '/dashboard'
        : '/employee_forms';
      
      // Direct navigation to ensure proper redirection
      window.location.href = redirectPath;
      
      // Fallback approaches with slight delays
      setTimeout(() => {
        window.location.href = window.location.origin + redirectPath;
      }, 50);
      
      setTimeout(() => {
        window.location.replace(redirectPath);
      }, 100);
      
    } catch (err) {
      // Fallback to regular navigation
      navigate("/dashboard");
    }
  };


  // Check WebAuthn support
  useEffect(() => {
    if (!window.PublicKeyCredential || !navigator.credentials) {
      setWebAuthnSupported(false);
      setError("WebAuthn is not supported in this browser. Please use a different browser or authentication method.");
    } else if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setError("WebAuthn requires a secure connection (HTTPS). Please access this site using https:// or contact your administrator.");
    }
  }, []);

  const checkWebAuthnStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/webauthn/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      setStep(data.isRegistered ? "login" : "register");
    } catch (err) {
      setError("Failed to initialize WebAuthn. Please try again.");
    }
  }, [email]); // email as dependency since it's used in the function

  useEffect(() => {
    if (!email) {
      navigate("/");
      return;
    }

    if (!isInitialSetup) {
      checkWebAuthnStatus();
    }
  }, [email, navigate, isInitialSetup, checkWebAuthnStatus]);
  const handleWebAuthnRegistration = async () => {
    setLoading(true);
    setError(null);
  
    try {
      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.');
      }

      // Check if we're in a secure context
      if (!window.isSecureContext) {
        throw new Error('WebAuthn requires HTTPS. Please access this site using https:// or use localhost for development.');
      }

      // Check if credentials API is available
      if (!navigator.credentials) {
        throw new Error('Credentials API is not available. Please check your browser settings.');
      }

      const optionsResponse = await fetch(`${API_URL}/auth/webauthn/register-request`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        },
        body: JSON.stringify({ email }),
      });
  
      if (!optionsResponse.ok) {
        const errorData = await optionsResponse.json();
        throw new Error(errorData.error || 'Failed to get registration options');
      }

      let registrationOptions = await optionsResponse.json();
  
      const publicKeyCredentialCreationOptions = {
        ...registrationOptions,
        challenge: base64ToArrayBuffer(registrationOptions.challenge),
        user: {
          ...registrationOptions.user,
          id: base64ToArrayBuffer(registrationOptions.user.id),
        },
      };
  
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });
  

  
      // Format the credential data for the server
      const attestationResponse = {
        id: credential.id,
        rawId: arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
          attestationObject: arrayBufferToBase64(credential.response.attestationObject),
        },
      };
  
      const verifyResponse = await fetch(`${API_URL}/auth/webauthn/register-verify`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        },
        body: JSON.stringify({
          email,
          attestationResponse,
        }),
      });
  
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.error || 'Failed to verify registration');
      }
  
      const verifyResult = await verifyResponse.json();
  
      // Handle registration completion
      if (verifyResult.success) {
        try {
          // Store complete user data
          localStorage.setItem('token', verifyResult.token);
          localStorage.setItem('user', JSON.stringify(verifyResult.user));
          localStorage.setItem('userRole', verifyResult.user.role);
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('justLoggedIn', 'true');
          
          if (verifyResult.employee) {
            localStorage.setItem('employeeId', String(verifyResult.employee.id));
            localStorage.setItem('employeeName', 
              `${verifyResult.employee.firstName} ${verifyResult.employee.lastName}`);
            localStorage.setItem('userStore', String(verifyResult.employee.locationId || ''));
          }
    
          // Call login with complete data as backup
          await login(
            verifyResult.token,
            verifyResult.user.role,
            verifyResult.employee?.locationId,
            verifyResult.employee?.id
          );
    
          // Determine redirect path based on role
          const redirectPath = (verifyResult.user.role === 'admin' || verifyResult.user.role === 'manager')
            ? '/dashboard'
            : '/employee_forms';
          
          // Direct navigation to ensure proper redirection
          window.location.href = redirectPath;
          
          // Fallback approaches with slight delays
          setTimeout(() => {
            window.location.href = window.location.origin + redirectPath;
          }, 50);
          
          setTimeout(() => {
            window.location.replace(redirectPath);
          }, 100);
          
        } catch (err) {
          // Fallback to regular navigation
          if (verifyResult.user.role === 'admin' || verifyResult.user.role === 'manager') {
            navigate('/dashboard');
          } else {
            navigate('/employee_forms');
          }
        }
      } else {
        throw new Error('Registration verification failed');
      }
  
    } catch (err) {
      setError(err.message || 'Failed to register security key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  
  // Update utility functions
  const handleWebAuthnLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check WebAuthn support first
      if (!webAuthnSupported || !navigator.credentials) {
        throw new Error('WebAuthn is not supported in this browser. Please use a different browser or authentication method.');
      }
  
      // 1. Get login options from server
      const optionsResponse = await fetch(`${API_URL}/auth/webauthn/login-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
  
      if (!optionsResponse.ok) {
        const errorData = await optionsResponse.json();
        throw new Error(errorData.error || 'Failed to get authentication options');
      }

      const assertionOptions = await optionsResponse.json();
      
      // Validate that we have the required challenge
      if (!assertionOptions.challenge) {
        throw new Error('No challenge received from server');
      }
  
      // Convert challenge from base64 to ArrayBuffer
      const challengeBuffer = base64ToArrayBuffer(assertionOptions.challenge);
  
      // 3. Request user authentication
      const credential = await navigator.credentials.get({
        publicKey: {
          ...assertionOptions,
          challenge: challengeBuffer,
          allowCredentials: assertionOptions.allowCredentials?.map(cred => ({
            id: cred.id ? base64ToArrayBuffer(cred.id) : null,
            type: 'public-key',
            transports: ['internal']
          })).filter(cred => cred.id !== null) || []
        }
      });
  
      // 4. Convert credential response before sending to server
      const authData = {
        id: credential.id,
        rawId: arrayBufferToBase64(credential.rawId),
        response: {
          authenticatorData: arrayBufferToBase64(credential.response.authenticatorData),
          clientDataJSON: arrayBufferToBase64(credential.response.clientDataJSON),
          signature: arrayBufferToBase64(credential.response.signature),
          userHandle: credential.response.userHandle 
            ? arrayBufferToBase64(credential.response.userHandle) 
            : null
        },
        type: credential.type
      };
  
      // 5. Verify login with server
      const verifyResponse = await fetch(`${API_URL}/auth/webauthn/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, assertionResponse: authData }),
      });
  
      if (!verifyResponse.ok) {
        const error = await verifyResponse.json();
        throw new Error(error.message || 'Failed to verify authentication');
      }
  
      const { token, user } = await verifyResponse.json();
  
      // Call handleSuccessfulLogin with the data
      handleSuccessfulLogin({ token, user });
  
    } catch (err) {
      console.error('WebAuthn error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Authentication was cancelled or timed out. Please try again.');
      } else if (err.name === 'InvalidStateError') {
        setError('No matching security key found. The key registered on another device cannot be used here. Please register a new key on this device.');
      } else if (err.name === 'NotFoundError') {
        setError('No security key found for this account on this device. You may need to register a new key.');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  

  const base64ToArrayBuffer = (base64String) => {
    try {
      // Check if base64String is valid
      if (!base64String || typeof base64String !== 'string') {
        throw new Error('Invalid base64 string provided to base64ToArrayBuffer');
      }

      // Convert base64url to base64
      const base64 = base64String
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/\s/g, '');
  
      // Add padding
      const pad = base64.length % 4;
      const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  
      const binary = atob(padded);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      
      return bytes.buffer;
    } catch (error) {
      console.error('base64ToArrayBuffer error:', error, 'Input:', base64String);
      throw new Error(`Failed to convert base64 to ArrayBuffer: ${error.message}`);
    }
  };
  
  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };
  


  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        padding: 2,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" sx={{ mb: 3 }}>
          {isInitialSetup
            ? "Security Key Setup Required"
            : step === "register"
            ? "Register Security Key"
            : "Security Key Login"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {isInitialSetup
              ? "To continue, you need to set up a security key for your account. Make sure your security key is connected and ready."
              : step === "register"
              ? "Please register your security key to continue. Make sure your security key is connected and ready."
              : "Please authenticate using your security key to continue."}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={
            step === "register"
              ? handleWebAuthnRegistration
              : handleWebAuthnLogin
          }
          disabled={loading || !webAuthnSupported}
          sx={{ mb: 2 }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : step === "register" ? (
            "Set Up Security Key"
          ) : (
            "Authenticate with Security Key"
          )}
        </Button>

        {!isInitialSetup && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => navigate("/")}
          >
            Back to Login
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default WebAuthnAuth;
